/**
 * Formats ISO-8601 {@code Duration} strings (e.g. 'P30D', 'P2W', 'PT48H') into
 * readable German plain text for the DunningType configuration overview.
 *
 * Affected field (requirement §7.2):
 *  - {@code DunningTypeStart.thresholdNegativePeriod}
 *
 * Why a dedicated function (requirement §13.5): the only existing core pipe,
 * {@code IsoDurationPipe}
 * ({@code core/shared/scheduler/components/event-component/pipe/iso-duration.pipe.ts}),
 * parses the TIME part only ({@code PT…H/M/S}) — 'P30D' yields an empty string
 * there — and emits English units ('hr', 'min', 'sec'). Both make it unusable
 * for this view (day-based periods, German UI). The core pipe is left untouched.
 *
 * @ticket bd-dunningtype-overview
 */

/** One parsed section of an ISO duration with its German singular/plural forms. */
interface DurationUnit {
    /** Numeric value of the section. */
    value: number;
    /** German label, singular. */
    singular: string;
    /** German label, plural. */
    plural: string;
}

/**
 * ISO-8601 duration: P[nY][nM][nW][nD][T[nH][nM][nS]].
 * The month part sits BEFORE the 'T', the minute part after it — both carry the
 * letter 'M' and are told apart by that position alone.
 */
const ISO_DURATION_PATTERN =
    /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;

/**
 * Translates an ISO-8601 duration string into German plain text.
 *
 * Examples:
 *  - 'P30D'    -> '30 Tage'
 *  - 'P1D'     -> '1 Tag'
 *  - 'P2W'     -> '2 Wochen'
 *  - 'P1M15D'  -> '1 Monat, 15 Tage'
 *  - 'PT48H'   -> '48 Stunden'
 *
 * Empty, missing or unparsable values return {@code null} so the calling
 * component decides on its own placeholder. A valid but empty period ('P0D',
 * 'PT0S') returns '0 Tage'.
 *
 * @param value ISO-8601 duration string as delivered by the backend
 * @returns German plain text, or {@code null} when nothing meaningful can be derived
 */
export function formatIsoDurationDe(value: string | null | undefined): string | null {
    if (!value) {
        return null;
    }

    const match = ISO_DURATION_PATTERN.exec(value.trim().toUpperCase());
    if (!match) {
        // Unknown format: pass the raw value through rather than claim something wrong.
        return value;
    }

    const units: DurationUnit[] = [
        {value: toNumber(match[1]), singular: 'Jahr', plural: 'Jahre'},
        {value: toNumber(match[2]), singular: 'Monat', plural: 'Monate'},
        {value: toNumber(match[3]), singular: 'Woche', plural: 'Wochen'},
        {value: toNumber(match[4]), singular: 'Tag', plural: 'Tage'},
        {value: toNumber(match[5]), singular: 'Stunde', plural: 'Stunden'},
        {value: toNumber(match[6]), singular: 'Minute', plural: 'Minuten'},
        {value: toNumber(match[7]), singular: 'Sekunde', plural: 'Sekunden'}
    ];

    const parts = units
        .filter(unit => unit.value > 0)
        .map(unit => `${unit.value} ${unit.value === 1 ? unit.singular : unit.plural}`);

    return parts.length ? parts.join(', ') : '0 Tage';
}

/** Converts an optional regex group into a number; a missing group counts as 0. */
function toNumber(group: string | undefined): number {
    return group ? Number(group) : 0;
}
